import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Button, 
  Form, 
  Alert, 
  Table, 
  Badge, 
  Spinner,
  Row,
  Col,
  Toast,
  ToastContainer,
  ButtonGroup
} from 'react-bootstrap';

const KeyGenerator = () => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [viewRegion, setViewRegion] = useState('GB');

  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    countryCode: 'GB',
    expiryDays: 365
  });

  const countryOptions = [
    { code: 'GB', name: 'United Kingdom' },
    { code: 'US', name: 'United States' },
    { code: 'IN', name: 'India' },
    { code: 'AE', name: 'United Arab Emirates' }
  ];

  // ✅ Keep this for display purposes only
  const regionMap = {
    GB: "eu-west-1",
    US: "us-west-2",
    IN: "ap-south-1",
    AE: "me-central-1"
  };

  useEffect(() => {
    fetchKeysFromRegion(viewRegion);
  }, [viewRegion]);

  // ✅ Send country code directly to backend
  const fetchKeysFromRegion = async (regionCode) => {
    const url = 'https://gsbt5qgrle.execute-api.eu-west-1.amazonaws.com/prod/api/admin/keys';
    const headers = {
      'Region': regionCode // ✅ Send "GB", "IN", "AE" instead of "eu-west-1", "ap-south-1"
    };

    console.log(`📡 [FETCH_KEYS_${regionCode}] Request:`, { url, headers });

    try {
      setLoading(true);
      const response = await fetch(url, { headers });

      console.log(`📡 [FETCH_KEYS_${regionCode}] Response status:`, response.status);

      if (!response.ok) {
        const text = await response.text();
        console.error(`❌ [FETCH_KEYS_${regionCode}] Error body:`, text);
        showNotification(`Error fetching ${regionCode} keys (HTTP ${response.status})`, 'danger');
        return;
      }

      const data = await response.json();
      console.log(`✅ [FETCH_KEYS_${regionCode}] Success:`, data);
      setKeys(data.map(key => ({ ...key, region: regionCode })));
    } catch (error) {
      console.error(`🚨 [FETCH_KEYS_${regionCode}] Network error:`, error);
      showNotification(`Error fetching ${regionCode} keys`, 'danger');
    } finally {
      setLoading(false);
    }
  };

  // ✅ Send country code directly to backend
  const fetchAllKeys = async () => {
    setLoading(true);
    const allKeys = [];
    
    for (const regionCode of Object.keys(regionMap)) {
      try {
        const url = 'https://gsbt5qgrle.execute-api.eu-west-1.amazonaws.com/prod/api/admin/keys';
        const headers = { 'Region': regionCode }; // ✅ Send "GB", "IN", "AE"
        
        console.log(`📡 [FETCH_ALL] Fetching ${regionCode} keys`);
        const response = await fetch(url, { headers });
        
        if (response.ok) {
          const data = await response.json();
          allKeys.push(...data.map(key => ({ ...key, region: regionCode })));
          console.log(`✅ [FETCH_ALL] Got ${data.length} keys from ${regionCode}`);
        } else {
          console.warn(`⚠️ [FETCH_ALL] Failed to fetch from ${regionCode}: ${response.status}`);
        }
      } catch (error) {
        console.error(`🚨 [FETCH_ALL] Error fetching from ${regionCode}:`, error);
      }
    }
    
    console.log(`✅ [FETCH_ALL] Total keys fetched: ${allKeys.length}`);
    setKeys(allKeys);
    setViewRegion('ALL');
    setLoading(false);
  };

  // ✅ Send country code directly to backend
  const handleGenerateKey = async () => {
    if (!formData.customerName || !formData.customerEmail) {
      showNotification('Please fill in customer name and email', 'danger');
      return;
    }

    const url = 'https://gl9sxio8nk.execute-api.eu-west-1.amazonaws.com/prod/api/admin/keys';
    const headers = {
      'Content-Type': 'application/json',
      'Region': formData.countryCode // ✅ Send "IN", "AE", "GB" instead of "ap-south-1"
    };
    const body = JSON.stringify(formData);

    console.log(`📡 [GENERATE_KEY_${formData.countryCode}] Request:`, { url, headers, body });

    setLoading(true);
    try {
      const response = await fetch(url, { method: 'POST', headers, body });
      console.log(`📡 [GENERATE_KEY_${formData.countryCode}] Response status:`, response.status);

      if (!response.ok) {
        const text = await response.text();
        console.error(`❌ [GENERATE_KEY_${formData.countryCode}] Error body:`, text);
        showNotification(`Error generating key (HTTP ${response.status})`, 'danger');
        return;
      }

      const newKey = await response.json();
      console.log(`✅ [GENERATE_KEY_${formData.countryCode}] Success:`, newKey);
      
      const keyWithRegion = { ...newKey, region: formData.countryCode };
      setKeys([...keys, keyWithRegion]);
      showNotification(`✅ License key generated in ${formData.countryCode} region!`, 'success');

      setViewRegion(formData.countryCode);

      setFormData({
        customerName: '',
        customerEmail: '',
        countryCode: 'GB',
        expiryDays: 365
      });
    } catch (error) {
      console.error(`🚨 [GENERATE_KEY_${formData.countryCode}] Network error:`, error);
      showNotification('Error generating key', 'danger');
    } finally {
      setLoading(false);
    }
  };

  // ✅ Send country code directly to backend
  const revokeKey = async (keyId, keyRegion) => {
    if (!window.confirm('Are you sure you want to revoke this key?')) return;

    const url = `https://tm0vlofuxd.execute-api.eu-west-1.amazonaws.com/prod/api/admin/keys/${keyId}/revoke`;
    const headers = {
      'Region': keyRegion // ✅ Send "IN", "AE", "GB" instead of "ap-south-1"
    };

    console.log(`📡 [REVOKE_KEY_${keyRegion}] Request:`, { url, headers });

    try {
      const response = await fetch(url, { method: 'PUT', headers });
      console.log(`📡 [REVOKE_KEY_${keyRegion}] Response status:`, response.status);

      if (!response.ok) {
        const text = await response.text();
        console.error(`❌ [REVOKE_KEY_${keyRegion}] Error body:`, text);
        showNotification(`Error revoking key (HTTP ${response.status})`, 'danger');
        return;
      }

      console.log(`✅ [REVOKE_KEY_${keyRegion}] Key revoked successfully`);
      setKeys(keys.map(key => 
        key.keyId === keyId ? { ...key, status: 'revoked' } : key
      ));
      showNotification('Key revoked successfully', 'success');
    } catch (error) {
      console.error(`🚨 [REVOKE_KEY_${keyRegion}] Network error:`, error);
      showNotification('Error revoking key', 'danger');
    }
  };

  const showNotification = (message, variant) => {
    setToastMessage(message);
    setToastVariant(variant);
    setShowToast(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showNotification('Key copied to clipboard!', 'info');
  };

  const getFilteredKeys = () => {
    if (viewRegion === 'ALL') {
      return keys;
    }
    return keys.filter(key => key.region === viewRegion);
  };

  return (
    <div>
      <Row>
        <Col lg={4}>
          <Card className="mb-4">
            <Card.Header>
              <h5>🔑 Generate SUSAN License Key</h5>
            </Card.Header>
            <Card.Body>
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Customer Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.customerName}
                    onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                    placeholder="Enter customer name"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Customer Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={formData.customerEmail}
                    onChange={(e) => setFormData({ ...formData, customerEmail: e.target.value })}
                    placeholder="Enter customer email"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Country/Region</Form.Label>
                  <Form.Select
                    value={formData.countryCode}
                    onChange={(e) => setFormData({ ...formData, countryCode: e.target.value })}
                  >
                    {countryOptions.map(country => (
                      <option key={country.code} value={country.code}>
                        {country.code} - {country.name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Region Code: <strong>{formData.countryCode}</strong> | AWS Region: <strong>{regionMap[formData.countryCode]}</strong>
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Expiry Days</Form.Label>
                  <Form.Control
                    type="number"
                    value={formData.expiryDays}
                    onChange={(e) => setFormData({ ...formData, expiryDays: parseInt(e.target.value) })}
                    min="1"
                  />
                </Form.Group>

                <Alert variant="info" className="small">
                  <strong>Region Code:</strong> {formData.countryCode}<br />
                  <strong>AWS Region:</strong> {regionMap[formData.countryCode]}<br />
                  <strong>Domain:</strong> Extracted from customer email<br />
                  <strong>Email:</strong> PDF license will be emailed automatically
                </Alert>

                <Button
                  variant="primary"
                  onClick={handleGenerateKey}
                  disabled={loading}
                  className="w-100"
                >
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Generating & Emailing...
                    </>
                  ) : (
                    `Generate Key (${formData.countryCode} Region)`
                  )}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={8}>
          <Card>
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <h5>📋 License Keys - {viewRegion === 'ALL' ? 'All Regions' : `${viewRegion} Region`}</h5>
                <Button variant="outline-secondary" size="sm" onClick={fetchAllKeys}>
                  🌍 Load All Regions
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <small className="text-muted d-block mb-2">View keys from:</small>
                <ButtonGroup>
                  {countryOptions.map(country => (
                    <Button
                      key={country.code}
                      variant={viewRegion === country.code ? "primary" : "outline-secondary"}
                      size="sm"
                      onClick={() => setViewRegion(country.code)}
                    >
                      {country.code}
                    </Button>
                  ))}
                  <Button
                    variant={viewRegion === 'ALL' ? "success" : "outline-success"}
                    size="sm"
                    onClick={() => fetchAllKeys()}
                  >
                    All Regions
                  </Button>
                </ButtonGroup>
              </div>

              {loading ? (
                <div className="text-center">
                  <Spinner animation="border" />
                  <p>Loading {viewRegion} keys...</p>
                </div>
              ) : (
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>License Key</th>
                      <th>Customer</th>
                      <th>Region</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getFilteredKeys().length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center text-muted">
                          {viewRegion === 'ALL' 
                            ? 'No keys found in any region' 
                            : `No keys found in ${viewRegion} region`
                          }
                        </td>
                      </tr>
                    ) : (
                      getFilteredKeys().map(key => (
                        <tr key={`${key.keyId}-${key.region}`}>
                          <td>
                            <code
                              style={{ cursor: 'pointer', fontSize: '14px' }}
                              onClick={() => copyToClipboard(key.licenseKey)}
                              title="Click to copy"
                            >
                              {key.licenseKey}
                            </code>
                          </td>
                          <td>
                            <div>
                              <strong>{key.customerName}</strong>
                              <br />
                              <small className="text-muted">{key.customerEmail}</small>
                            </div>
                          </td>
                          <td>
                            <div>
                              <Badge bg="secondary">{key.region}</Badge>
                              <br />
                              <small className="text-muted">{regionMap[key.region]}</small>
                            </div>
                          </td>
                          <td>
                            <Badge bg={key.status === 'active' ? 'success' : 'danger'}>
                              {key.status}
                            </Badge>
                          </td>
                          <td>
                            <small>{new Date(key.createdAt).toLocaleDateString()}</small>
                          </td>
                          <td>
                            {key.status === 'active' && (
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => revokeKey(key.keyId, key.region)}
                              >
                                Revoke
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <ToastContainer position="top-end" className="p-3">
        <Toast
          show={showToast}
          onClose={() => setShowToast(false)}
          bg={toastVariant}
          autohide
          delay={4000}
        >
          <Toast.Body className="text-white">{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

export default KeyGenerator;
